import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAfterNextRender, useControlledState, usePersistentCallback } from '@prophecy/utils/react/hooks';
import { Children, useContext, createContext, isValidElement, useRef, forwardRef, useEffect } from 'react';
import { Stack } from '../Layout/Stack';
import { Separator } from '../Separator';
import { LabelWithHint } from '../Typography/LabelWithHint';
import { StyledTabsRoot, StyledTabListContainer, StyledTabsList, StyledTabsTrigger, StyledTabsContentWrap, StyledTabsContent, StyledTabContainerStackItem, StyledTabsTriggerGroup, StyledTriggerGroupLabel, StyledBox, StyledTriggerButton } from './styled';
import { tokens } from './tokens';
import { TriggerType, TabVariant, TabSize, Direction } from './types';
const tabContextDefaults = {
    size: TabSize.m,
    variant: TabVariant.brand,
    orientation: Direction.horizontal,
    isTabBordered: true
};
export const TAB_CONTENT_WRAPPER_CLASS_NAME = 'tab-content-wrapper';
export const TAB_LIST_CONTAINER_CLASS_NAME = 'tab-list-container';
export const TabPaneAttrs = {
    key: 'data-tab-pane-key'
};
const TabsContext = createContext(tabContextDefaults);
export const TabsRoot = forwardRef(({ size, variant, orientation, stretchTabs, bordered, isTabBordered, ...restProps }, ref) => {
    const _orientation = orientation || tabContextDefaults.orientation;
    return (_jsx(TabsContext.Provider, { value: {
            size: size || tabContextDefaults.size,
            variant: variant || tabContextDefaults.variant,
            orientation: _orientation,
            // for vertical orientation ignore stretchTabs props
            stretchTabs: _orientation === Direction.horizontal ? stretchTabs : undefined,
            isTabBordered: isTabBordered ?? tabContextDefaults.isTabBordered
        }, children: _jsx(StyledTabsRoot, { ref: ref, variant: variant, orientation: _orientation, bordered: bordered, ...restProps }) }));
});
export const TabsList = forwardRef((props, _ref) => {
    const { variant, orientation, stretchTabs } = useContext(TabsContext);
    return _jsx(StyledTabsList, { ...props, variant: variant, orientation: orientation, stretchTabs: stretchTabs });
});
export const TabsTrigger = (props) => {
    const { size, variant, orientation, stretchTabs, isTabBordered } = useContext(TabsContext);
    return (_jsx(StyledTabsTrigger, { size: size, variant: variant, isTabBordered: isTabBordered, orientation: orientation, stretchTabs: stretchTabs, ...props }));
};
export const TabsContent = StyledTabsContent;
export function TabsTriggerGroup({ label, children, selected }) {
    const { size, variant } = useContext(TabsContext);
    return (_jsxs(StyledTabsTriggerGroup, { selected: Boolean(selected), size: size, variant: variant, children: [_jsx(StyledTriggerGroupLabel, { size: size, children: label }), children] }));
}
function getKeyFromTrigger(trigger) {
    if (!trigger) {
        return;
    }
    else if (trigger.type === TriggerType.trigger) {
        return trigger.props.value;
    }
    else if (trigger.type === TriggerType.group) {
        return getKeyFromTrigger(trigger.props.childTriggers[0]);
    }
}
function extractTabsFromChildren(children) {
    const triggers = [];
    const contents = [];
    Children.map(children, (child) => {
        if (!isValidElement(child))
            return;
        const { key, props } = child;
        const { tab, children, ...restProps } = props;
        if (tab) {
            triggers.push({
                type: TriggerType.trigger,
                props: { ...restProps, children: tab, value: key }
            });
            contents.push({ value: key, children });
        }
        else if (children) {
            const { triggers: childTriggers, contents: childContents } = extractTabsFromChildren(children);
            // add only if has nested triggers
            if (!childTriggers.length)
                return;
            triggers.push({ type: TriggerType.group, props: { ...restProps, childTriggers } });
            contents.push(...childContents);
        }
    });
    return { triggers, contents };
}
function TriggerWrap({ value, hint, ...restProps }) {
    let btnUI = (_jsx(StyledTriggerButton, { variant: 'plain', "data-value": value, ...restProps, "data-test-id": `tabs-trigger-button-${value}` }));
    if (hint) {
        btnUI = _jsx(LabelWithHint, { hint: hint, children: btnUI });
    }
    return (_jsx(TabsTrigger, { value: value, disabled: restProps.disabled, asChild: true, children: btnUI }, value));
}
export function Tabs({ children, activeKey, defaultActiveKey, onChange, tabBarExtraContent = {}, variant, size = tabContextDefaults.size, orientation = tabContextDefaults.orientation, stretchTabs, bordered, scrollableTabs = false, separator, ...restProps }) {
    const { triggers, contents } = extractTabsFromChildren(children);
    const [selectedTab, onTabChange] = useControlledState({
        value: activeKey,
        defaultValue: defaultActiveKey ?? getKeyFromTrigger(triggers[0]),
        onChange: onChange
    });
    const tabRef = useRef(null);
    const tabListRef = useRef(null);
    const { afterNextRender } = useAfterNextRender();
    const scrollTabIntoView = usePersistentCallback(() => {
        const tabListElement = tabListRef.current;
        if (!tabListElement)
            return;
        const selectedTabElement = tabListElement.querySelector(`[data-value='${selectedTab}']`);
        if (!selectedTabElement)
            return;
        afterNextRender(() => {
            if (orientation === 'horizontal') {
                tabListElement.scrollTo({
                    left: selectedTabElement.offsetLeft - tabListElement.offsetLeft
                });
            }
            else {
                tabListElement.scrollTo({
                    top: selectedTabElement.offsetTop - tabListElement.offsetTop
                });
            }
        });
    });
    // if the tab has scroll bar then we need to scroll to the selected tab on mount
    useEffect(() => {
        if (scrollableTabs)
            scrollTabIntoView();
    }, [scrollableTabs, scrollTabIntoView]);
    const hasRightContentInsideTab = tabBarExtraContent.hasRightContentInsideTab;
    const getTabsUI = () => {
        return (_jsxs(_Fragment, { children: [triggers.map(({ type, props }, index) => {
                    if (type === TriggerType.group) {
                        const { label, childTriggers } = props;
                        return (_jsx(TabsTriggerGroup, { label: label, selected: childTriggers.some((trigger) => trigger.props.value === selectedTab), children: childTriggers.map((trigger, index) => (_jsx(TriggerWrap, { ...trigger.props }, index))) }, index));
                    }
                    const _props = props;
                    return _jsx(TriggerWrap, { ..._props }, index);
                }), separator && _jsx(Separator, {})] }));
    };
    return (_jsxs(TabsRoot, { ...restProps, dir: 'ltr', ref: tabRef, value: selectedTab, onValueChange: onTabChange, variant: variant, size: size, bordered: bordered, stretchTabs: stretchTabs, activationMode: 'manual', orientation: orientation, children: [_jsxs(StyledTabListContainer, { direction: orientation, className: TAB_LIST_CONTAINER_CLASS_NAME, gap: tokens.TabsTrigger.size[size].padding, alignY: orientation === 'horizontal' ? 'center' : 'stretch', children: [!tabBarExtraContent?.hasLeftContentInsideTab && tabBarExtraContent.left, _jsx(StyledTabContainerStackItem, { grow: '1', shrink: scrollableTabs ? '1' : '0', children: _jsxs(TabsList, { ref: tabListRef, children: [tabBarExtraContent?.hasLeftContentInsideTab && tabBarExtraContent.left, hasRightContentInsideTab && orientation === 'horizontal' ? (_jsxs(Stack, { direction: 'horizontal', align: 'space-between', alignY: 'center', width: '100%', children: [_jsx(Stack, { direction: 'horizontal', alignY: 'center', grow: '1', children: getTabsUI() }), tabBarExtraContent.right] })) : (_jsxs(_Fragment, { children: [getTabsUI(), hasRightContentInsideTab && tabBarExtraContent.right] }))] }) }), !hasRightContentInsideTab && tabBarExtraContent.right] }), _jsx(StyledTabsContentWrap, { grow: '1', className: TAB_CONTENT_WRAPPER_CLASS_NAME, children: contents.map(({ value, children }) => {
                    const attrPros = { [TabPaneAttrs.key]: value };
                    return (_createElement(TabsContent, { ...attrPros, value: value, key: value, tabIndex: -1 },
                        _jsx(StyledBox, { children: children })));
                }) })] }));
}
export function TabPane(_props) {
    return null;
}
export function TabPaneGroup(_props) {
    return null;
}
Tabs.TabPane = TabPane;
Tabs.TabPaneGroup = TabPaneGroup;
